import { Injectable, Injector } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AdAdminService, HomepageContentResponse } from 'src/app/admin/ad-admin/ad-admin.service';
import { readLayoutDisplayId } from 'src/app/admin/ad-admin/ad-layout/ad-layout.models';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
import { TfService } from 'src/app/tf/tf-service.service';
import { CfHomeService } from './cf-home.service';
import { VisitorCountryService } from 'src/app/Theme/shared/service/visitor-country.service';

export interface CfHomePortalSectionConfig {
  key?: string;
  position?: number;
  hidden?: boolean;
  nbrProduct?: number;
  title?: unknown;
  description?: unknown;
  showCta?: boolean;
  itemsDisplayed?: number;
  images?: Array<{ url?: string; id?: number | string; default?: boolean }>;
  products?: Array<{
    itemCode?: string;
    imageUrl?: string;
    title?: string;
    description?: string;
  }>;
  topics?: unknown[];
}

/** Loads public homepage content once per session (country-scoped). */
@Injectable({
  providedIn: 'root',
})
export class CfHomeContentService {
  private static readonly THEME_HOMEPAGE_MOBILE_KEY = 'homepage-mobile';
  private static readonly MOBILE_MEDIA_QUERY = '(max-width: 767.98px)';

  private loadInFlight: Observable<HomepageContentResponse | null> | null = null;
  private cachedContent: HomepageContentResponse | null = null;
  private cachedCountryCode: string | null | undefined;
  private cachedSections: CfHomePortalSectionConfig[] | null = null;
  private cachedMobileSections: CfHomePortalSectionConfig[] | null = null;
  /** Auth state when the cache was populated (logout/login must invalidate). */
  private cachedForAuthenticated: boolean | undefined;

  constructor(
    private readonly cfHomeService: CfHomeService,
    private readonly visitorCountry: VisitorCountryService,
    private readonly portalLayoutResolve: PortalLayoutResolveService,
    private readonly injector: Injector,
    private readonly adAdminService: AdAdminService,
  ) {}

  /** Combined homepage payload for the detected visitor country. */
  load(): Observable<HomepageContentResponse | null> {
    this.invalidateCacheIfAuthChanged();

    if (this.cachedContent) {
      return of(this.cachedContent);
    }
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.visitorCountry.getCountryCode().pipe(
      switchMap((countryCode) => {
        this.cachedCountryCode = countryCode;
        const layoutDisplayId =
          readLayoutDisplayId(this.portalLayoutResolve.getEntry('homepage')) || null;
        return this.cfHomeService.getHomepageContent({ countryCode, layoutDisplayId }).pipe(
          switchMap((content) =>
            this.loadSectionConfig(countryCode).pipe(
              catchError(() => of({ desktop: null, mobile: null })),
              map((sectionConfig) => {
                this.cachedSections = sectionConfig.desktop;
                this.cachedMobileSections = sectionConfig.mobile;
                return content;
              }),
            ),
          ),
        );
      }),
      tap((content) => {
        this.cachedContent = content;
        this.cachedForAuthenticated = this.isAuthenticated();
      }),
      catchError((err) => {
        console.warn('[CfHomeContent] Failed to load homepage content', err);
        return of(null);
      }),
      finalize(() => {
        this.loadInFlight = null;
      }),
      shareReplay(1),
    );

    return this.loadInFlight;
  }

  /** Clears cache and loads fresh (e.g. after logout → /home). */
  reload(): Observable<HomepageContentResponse | null> {
    this.clearCache();
    return this.load();
  }

  getCachedContent(): HomepageContentResponse | null {
    return this.cachedContent;
  }

  getCachedCountryCode(): string | null | undefined {
    return this.cachedCountryCode;
  }

  getCachedSections(): CfHomePortalSectionConfig[] | null {
    if (this.isMobileViewport()) {
      return this.cachedMobileSections ?? this.cachedSections;
    }
    return this.cachedSections;
  }

  getLayoutViewport(): 'desktop' | 'mobile' {
    return this.isMobileViewport() ? 'mobile' : 'desktop';
  }

  getCachedDesktopSections(): CfHomePortalSectionConfig[] | null {
    return this.cachedSections;
  }

  getCachedMobileSections(): CfHomePortalSectionConfig[] | null {
    return this.cachedMobileSections;
  }

  isMobileViewport(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia(CfHomeContentService.MOBILE_MEDIA_QUERY).matches;
  }

  clearCache(): void {
    this.cachedContent = null;
    this.cachedCountryCode = undefined;
    this.cachedSections = null;
    this.cachedMobileSections = null;
    this.cachedForAuthenticated = undefined;
    this.loadInFlight = null;
  }

  private invalidateCacheIfAuthChanged(): void {
    if (this.cachedContent == null) {
      return;
    }
    const isAuthenticated = this.isAuthenticated();
    if (this.cachedForAuthenticated !== isAuthenticated) {
      this.clearCache();
    }
  }

  /** Lazy resolve avoids circular DI with TfService (which clears this cache on logout). */
  private isAuthenticated(): boolean {
    return this.injector.get(TfService).getCurrentAuthInfo().isAuthenticated;
  }

  /** Section order/visibility from layout payload (optional; falls back to defaults). */
  private loadSectionConfig(
    countryCode: string | null,
  ): Observable<{ desktop: CfHomePortalSectionConfig[] | null; mobile: CfHomePortalSectionConfig[] | null }> {
    const homepage$ = countryCode
      ? this.portalLayoutResolve
          .resolveOne({ component: 'homepage', country: countryCode })
          .pipe(catchError(() => of(null)))
      : of(null);
    const theme$ = this.adAdminService.getDataS3File().pipe(catchError(() => of(null)));

    return forkJoin({ homepage: homepage$, theme: theme$ }).pipe(
      map(({ homepage, theme }) => {
        const desktop = this.readSections(homepage?.payload);
        const mobileFromAd = this.readNestedMobileSections(homepage?.payload);
        const mobileFromTheme = this.readThemeHomepageMobileSections(theme);
        const mobile = this.resolveIndependentMobileSections(desktop, mobileFromAd ?? mobileFromTheme);
        return { desktop, mobile };
      }),
    );
  }

  private readSections(payload: Record<string, unknown> | null | undefined): CfHomePortalSectionConfig[] | null {
    const sections = payload?.['sections'];
    return Array.isArray(sections) && sections.length
      ? (sections as CfHomePortalSectionConfig[])
      : null;
  }

  private resolveIndependentMobileSections(
    desktop: CfHomePortalSectionConfig[] | null,
    mobile: CfHomePortalSectionConfig[] | null,
  ): CfHomePortalSectionConfig[] | null {
    if (!mobile?.length) {
      return null;
    }
    if (desktop?.length && JSON.stringify(mobile) === JSON.stringify(desktop)) {
      return null;
    }
    return mobile;
  }

  private readNestedMobileSections(
    payload: Record<string, unknown> | null | undefined,
  ): CfHomePortalSectionConfig[] | null {
    const nested = payload?.['mobile'];
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      return null;
    }
    return this.readSections(nested as Record<string, unknown>);
  }

  private readThemeHomepageMobileSections(theme: unknown): CfHomePortalSectionConfig[] | null {
    const root = theme as { document?: { layoutData?: Record<string, unknown> } } | null;
    const mobile = root?.document?.layoutData?.[CfHomeContentService.THEME_HOMEPAGE_MOBILE_KEY];
    if (!mobile || typeof mobile !== 'object' || Array.isArray(mobile)) {
      return null;
    }
    return this.readSections(mobile as Record<string, unknown>);
  }
}
